import { useEffect, useState } from "react";
import { X, Loader2, Wallet, Sparkles } from "lucide-react";
import SidePanel from "./SidePanel";
import {formatINR,
    getMembershipFee,
    getMembershipGST,
    getMembershipTotal,
    membershipFeeText,
    MembershipSummary,
    getCashbackAmount,
    getRewardCashback} from "./VoucherCheckoutPanel";

import { getBestReward, getNextReward, nextRewardLabel, round2 } from "../../utils/rewardUtils";
const MAX_BILL_AMOUNT = 10000000; // ₹1 crore
/**
 * Pay Bill drawer: the customer types their bill amount, a cash voucher is generated after payment.
 * bestPercentOff: the store's best % reward (same value the "Pay Bill · Save X%" button shows)
 * maxDiscount: max discount cap (₹) of that reward; discount never exceeds it (empty/0 = no cap)
 * rewardId: id of the reward being applied (discount + cashback come from it); passed back to onPay
 * cashback: { type, value, max } of the reward being applied (see getRewardCashback), earned on the bill amount; null = none
 * membership: { name, fee, owned } - a membership is required to pay; its fee (0 if free / already owned) is added to the total
 * onChangeMembership: optional, shows a "Change" link on the membership card
 * onPay({ rewardId, amount, discount, payable, cashback, membershipFee, total }) - cashback is the ₹ earned; payable is the bill after discount, total = payable + membershipFee; may return a promise
 */
export default function PayBillPanel({ open,
    onClose,
    store,
    rewards = [],
    membership = null,
    onChangeMembership,
    onPay}) {
    const [amount, setAmount] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const [limitHit, setLimitHit] = useState(false);
   // Start fresh every time the panel is reopened
    useEffect(() => {
        if (!open) {
            setAmount("");
            setLimitHit(false);
        }
    }, [open]);

    const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/[^\d.]/g, "");
    const [whole, ...rest] = raw.split(".");
    const cleaned = rest.length ? `${whole}.${rest.join("").slice(0, 2)}` : whole;

    // Anything above ₹1 crore is rejected (typing or pasting): the field keeps its last valid value
    if ((parseFloat(cleaned) || 0) > MAX_BILL_AMOUNT) {
        setLimitHit(true);
        return;
    }
    setLimitHit(false);
    setAmount(cleaned.slice(0, 10));
    };
   
    const value = parseFloat(amount) || 0;

    const best = getBestReward(rewards, value);
    const applicableReward = best?.reward || null;
    const rewardId = applicableReward?.reward_id ?? null;

    const isFixed = best?.isFixed || false;
    const pct = best?.pct || 0;
    const cap = best?.cap || 0;
    const capApplied = best?.capApplied || false;
    const discount = best?.discount || 0;
    const payable = round2(value - discount);

    const cashback = applicableReward
        ? getCashbackAmount(getRewardCashback(applicableReward), value)
        : 0;
    const cashbackAmount = cashback || 0;

    const membershipFee = getMembershipFee(membership);
    const membershipGST = getMembershipGST(membership);
    const membershipTotal = getMembershipTotal(membership);
    const total = round2(payable + membershipTotal);
    const canPay = value > 0 && value <= MAX_BILL_AMOUNT && !submitting;

    const next = value > 0 ? getNextReward(rewards, value, discount, applicableReward) : null;
    const nextReward = next?.reward || null;
    const amountToUnlockNextReward = next?.need || 0;

    const address = [store?.address, store?.cityName].filter(Boolean).join(", ");

    const handlePay = async () => {
        if (!canPay) return;
        setSubmitting(true);
        try {
            await onPay?.({ rewardId, amount: value, discount, payable, cashback: cashbackAmount, membershipFee, total, membershipGST,
            membershipTotal });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <SidePanel open={open} onClose={onClose} label="Pay bill">
            <div className="shrink-0 rounded-b-3xl bg-[#0B67FC] px-5 pt-5 text-white">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="truncate text-xl font-bold">{store?.store_name}</h2>
                        {address && <p className="mt-0.5 line-clamp-2 text-xs text-white/80">{address}</p>}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 rounded-full bg-white p-1.5 text-gray-800 shadow-sm transition-colors hover:bg-gray-100"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="mt-8 flex items-center justify-center gap-1">
                    <span className="text-4xl font-bold">₹</span>
                    <input
                        type="text"
                        inputMode="decimal"
                        autoFocus
                        value={amount}
                        onChange={handleAmountChange}
                        placeholder="Amount"
                        aria-label="Bill amount"
                        className="w-44 bg-transparent text-4xl font-bold text-white placeholder:text-white/70 focus:outline-none"
                    />
                </div>
                {limitHit && (
                    <p className="mt-2 text-center text-xs font-medium text-amber-200">
                        Maximum bill amount is {formatINR(MAX_BILL_AMOUNT)}
                    </p>
                )}
                <div className="mt-6 flex justify-center">
                    
                    <span className="rounded-t-xl bg-black/20 px-10 py-2 text-md font-bold">
                        {discount > 0
                            ? isFixed
                                ? `Save ${formatINR(discount)}`
                                : `Save ${pct}%${cap > 0 ? ` (max ${formatINR(cap)})` : ""}`
                            : "Pay your bill"}
                    </span>
                </div>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
                {membership?.owned?'':<MembershipSummary membership={membership} onChange={onChangeMembership} />}

                {cashback > 0 && (
                    <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">
                        <Wallet size={16} className="shrink-0" />
                        Earn {formatINR(cashback)} cashback on this bill
                    </div>
                )}

                {value > 0 && (
                    <div className="space-y-2.5 rounded-2xl border border-gray-100 bg-white p-4 text-sm shadow-2xs">
                         {nextReward && amountToUnlockNextReward > 0 && (
                            <div className="mt-3 rounded-xl border border-[#0B67FC]/20 bg-[#0B67FC]/5 px-4 py-3">
                                <div className="flex items-start gap-2">
                                    <Sparkles
                                        size={16}
                                        className="mt-0.5 shrink-0 text-[#0B67FC]"
                                    />

                                    <div>
                                       <p className="text-sm font-bold text-[#062E6C]">
    Spend {formatINR(amountToUnlockNextReward)} more
</p>
<p className="mt-0.5 text-xs font-medium text-[#0954D0]">
    to get {nextRewardLabel(next)} OFF
</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="flex items-center justify-between text-gray-700">
                            <span>Bill amount</span>
                            <span className="font-semibold text-gray-900">{formatINR(value)}</span>
                        </div>
                        {discount > 0 && (
                            <div className="flex items-center justify-between text-emerald-700">
                                <span>
                                    {isFixed
                                        ? "You save"
                                        : capApplied
                                        ? `You save (max ${formatINR(cap)})`
                                        : `You save (${pct}%)`}
                                </span>
                                <span className="font-semibold">-{formatINR(discount)}</span>
                            </div>
                        )}
                       
                        {membership && !membership.owned && (
                            <>
                                {membership.credit > 0 && (
                                    <>
                                        <div className="flex items-center justify-between text-gray-700">
                                            <span>{membership.name} membership</span>
                                            <span className="font-semibold text-gray-900">{formatINR(membership.fullFee)}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-emerald-700">
                                            <span>Credit for {membership.creditDays} days left on your plan</span>
                                            <span className="font-semibold">-{formatINR(membership.credit)}</span>
                                        </div>
                                    </>
                                )}
                                <div className="flex items-center justify-between text-gray-700">
                                    <span>Membership Fee</span>
                                    <span className={`font-semibold ${membershipFee > 0 ? "text-gray-900" : "text-emerald-700"}`}>
                                        {membershipFee > 0
                                            ? formatINR(membershipFee)
                                            : membershipFeeText(membership)}
                                    </span>
                                </div>

                                {membershipFee > 0 && (
                                    <div className="flex items-center justify-between text-gray-700">
                                        <span>GST on Membership (18%)</span>
                                        <span className="font-semibold text-gray-900">
                                            {formatINR(membershipGST)}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                        <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                            <span className="text-gray-700">To pay</span>
                            <span className="text-xl font-bold text-gray-900">{formatINR(total)}</span>
                        </div>
                        {cashbackAmount > 0 && (
                            <div className="flex items-center justify-between text-emerald-700">
                                <span>Cashback you'll earn</span>
                                <span className="font-semibold">{formatINR(cashbackAmount)}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="border-t border-gray-100 bg-white px-5 py-4">
                <button
                    type="button"
                    onClick={handlePay}
                    disabled={!canPay}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B67FC] py-3.5 text-base font-bold text-white shadow-sm transition-all hover:bg-[#0954D0] active:scale-[0.99] disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none"
                >
                    {submitting && <Loader2 size={18} className="animate-spin" />}
                    Pay ₹ {value > 0 ? total.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "--"}
                </button>
            </div>
        </SidePanel>
    );
}